import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  OutlinedInput,
  InputLabel
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useMutation, useQuery } from 'react-query';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { QRCode } from 'react-qrcode-logo';
import apiClient from '../../request/http-common';

const JointAccountDeposit = ({ open, onClose, jointAccountId, onSuccess }) => {
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [amount, setAmount] = useState('');
  const [copied, setCopied] = useState(false);
  const [currentStep, setCurrentStep] = useState('select'); // select, deposit, confirm

  const addresses = useSelector((state) => state.coins.addresses);
  const coins = useSelector((state) => state.coins.coins);

  // Available coins for deposit
  const availableCoins = [
    { 
      name: 'Bitcoin', 
      symbol: 'BTC', 
      image: 'https://cryptologos.cc/logos/bitcoin-btc-logo.png',
      network: 'Bitcoin'
    },
    { 
      name: 'Ethereum', 
      symbol: 'ETH', 
      image: 'https://cryptologos.cc/logos/ethereum-eth-logo.png',
      network: 'Ethereum'
    },
    { 
      name: 'USDC', 
      symbol: 'USDC', 
      image: 'https://res.cloudinary.com/dyz8kppso/image/upload/v1752850333/dc07jds0ueozxgsbrzrf.png',
      network: 'ERC20'
    },
    { 
      name: 'USDT', 
      symbol: 'USDT', 
      image: 'https://cryptologos.cc/logos/tether-usdt-logo.png',
      network: 'TRC20'
    },
    { 
      name: 'Binance Coin', 
      symbol: 'BNB', 
      image: 'https://cryptologos.cc/logos/binance-coin-bnb-logo.png',
      network: 'BSC'
    }
  ];

  const { mutate: createDeposit, isLoading: isCreating } = useMutation(
    async (depositData) => {
      const response = await apiClient.post(`/api/joint-accounts/${jointAccountId}/transactions`, {
        transaction_type: 'deposit',
        amount: parseFloat(depositData.amount),
        description: `${depositData.coin} deposit to joint account`,
        metadata: {
          coin: depositData.coin,
          network: depositData.network,
          deposit_address: depositData.address
        }
      });
      return response.data;
    },
    {
      onSuccess: () => {
        setCurrentStep('confirm');
        onSuccess?.();
      },
      onError: (error) => {
        console.error('Deposit creation failed:', error);
      }
    }
  );

  const getDepositAddress = (coinSymbol) => {
    // Get address from the addresses state or use default
    const coinAddress = addresses?.find(addr => 
      addr.coin_name?.toLowerCase() === coinSymbol.toLowerCase()
    );
    return coinAddress?.address || generateDefaultAddress(coinSymbol);
  };

  const generateDefaultAddress = (coinSymbol) => {
    // Default addresses for demo purposes
    const defaultAddresses = {
      'BTC': 'bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh',
      'ETH': '0x742d35Cc6634C0532925a3b8D0884fa8F6835b84',
      'USDC': '0x742d35Cc6634C0532925a3b8D0884fa8F6835b84',
      'USDT': 'TN1PwBzaGnJZz1Q3qJRYC1JHjkc6wVvf8s',
      'BNB': 'bnb136ns6lfw4zs5hg4n85vdthaad7hq5m4gtkgf23'
    };
    return defaultAddresses[coinSymbol] || 'Address not available';
  };

  const handleCoinSelect = (coin) => {
    setSelectedCoin(coin);
    setCurrentStep('deposit');
  };

  const handleDepositSubmit = () => {
    if (!amount || parseFloat(amount) <= 0) {
      return;
    }

    createDeposit({
      coin: selectedCoin.symbol,
      network: selectedCoin.network,
      amount: amount,
      address: getDepositAddress(selectedCoin.symbol)
    });
  };

  const handleClose = () => {
    setCurrentStep('select');
    setSelectedCoin(null);
    setAmount('');
    setCopied(false);
    onClose();
  };

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
          border: '1px solid #2B3139',
          borderRadius: '16px'
        }
      }}
    >
      <DialogTitle sx={{ 
        color: '#FFFFFF', 
        borderBottom: '1px solid #2B3139',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <AccountBalanceIcon sx={{ color: '#F7931A' }} />
        Deposit to Joint Account
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {currentStep === 'select' && (
          <Box>
            <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 2 }}>
              Select Cryptocurrency
            </Typography>
            <Typography variant="body2" sx={{ color: '#6C7293', mb: 3 }}>
              Choose the cryptocurrency you want to deposit to this joint account
            </Typography>

            <List sx={{ width: '100%' }}>
              {availableCoins.map((coin, index) => (
                <React.Fragment key={coin.symbol}>
                  <ListItem
                    onClick={() => handleCoinSelect(coin)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: '8px',
                      mb: 1,
                      '&:hover': {
                        backgroundColor: 'rgba(247, 147, 26, 0.1)',
                        transform: 'translateX(4px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={coin.image}
                        sx={{ width: 40, height: 40 }}
                      >
                        {coin.symbol[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ color: '#FFFFFF' }}>
                          {coin.name} ({coin.symbol})
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ color: '#6C7293' }}>
                          Network: {coin.network}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < availableCoins.length - 1 && (
                    <Divider sx={{ borderColor: '#2B3139' }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}

        {currentStep === 'deposit' && selectedCoin && (
          <Box>
            <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 2 }}>
              Deposit {selectedCoin.name}
            </Typography>

            <Card sx={{
              background: 'linear-gradient(135deg, #1E2328 0%, #2B3139 100%)',
              border: '1px solid #3B4148',
              mb: 3
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar
                  src={selectedCoin.image}
                  sx={{ width: 60, height: 60, mx: 'auto', mb: 2 }}
                >
                  {selectedCoin.symbol[0]}
                </Avatar>
                <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 1 }}>
                  {selectedCoin.name} ({selectedCoin.symbol})
                </Typography>
                <Typography variant="body2" sx={{ color: '#6C7293' }}>
                  Network: {selectedCoin.network}
                </Typography>
              </CardContent>
            </Card>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel sx={{ color: '#6C7293' }}>Amount to Deposit</InputLabel>
              <OutlinedInput
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                type="number"
                placeholder="0.00"
                sx={{
                  color: '#FFFFFF',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2B3139'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#F7931A'
                  }
                }}
              />
            </FormControl>

            <Box sx={{
              background: 'linear-gradient(135deg, #1E2328 0%, #2B3139 100%)',
              border: '1px solid #3B4148',
              borderRadius: '12px',
              p: 3,
              mb: 3
            }}>
              <Typography variant="subtitle1" sx={{ color: '#FFFFFF', mb: 2 }}>
                Deposit Address
              </Typography>
              
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <QRCode
                  value={getDepositAddress(selectedCoin.symbol)}
                  size={150}
                  bgColor="#1E2328"
                  fgColor="#FFFFFF"
                  logoImage={selectedCoin.image}
                  logoWidth={30}
                  logoHeight={30}
                />
              </Box>

              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 2,
                background: '#161A1E',
                borderRadius: '8px',
                border: '1px solid #2B3139'
              }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#FFFFFF',
                    wordBreak: 'break-all',
                    flex: 1,
                    fontSize: '0.875rem'
                  }}
                >
                  {getDepositAddress(selectedCoin.symbol)}
                </Typography>
                <CopyToClipboard
                  text={getDepositAddress(selectedCoin.symbol)}
                  onCopy={handleCopy}
                >
                  <Button
                    size="small"
                    sx={{
                      color: copied ? '#10B981' : '#F7931A',
                      minWidth: 'auto',
                      p: 1
                    }}
                  >
                    {copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                  </Button>
                </CopyToClipboard>
              </Box>
            </Box>

            <Alert severity="warning" sx={{ mb: 3 }}>
              Only send {selectedCoin.symbol} to this address on the {selectedCoin.network} network. 
              Sending other cryptocurrencies may result in permanent loss.
            </Alert>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setCurrentStep('select')}
                sx={{
                  borderColor: '#6C7293',
                  color: '#6C7293',
                  flex: 1
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleDepositSubmit}
                disabled={!amount || parseFloat(amount) <= 0 || isCreating}
                sx={{
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  flex: 1,
                  '&:disabled': {
                    background: '#2B3139'
                  }
                }}
              >
                {isCreating ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: '#FFFFFF' }} />
                    Processing...
                  </>
                ) : (
                  'Confirm Deposit'
                )}
              </Button>
            </Box>
          </Box>
        )}

        {currentStep === 'confirm' && (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: '#10B981', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 2 }}>
              Deposit Request Submitted
            </Typography>
            <Typography variant="body2" sx={{ color: '#6C7293', mb: 3 }}>
              Your deposit request has been submitted and is pending admin verification. 
              You will be notified once the deposit is confirmed.
            </Typography>
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                background: 'linear-gradient(135deg, #F7931A 0%, #FFB800 100%)'
              }}
            >
              Close
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default JointAccountDeposit; 